import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AUTH_EXPIRED_EVENT, ApiError, authApi, tokenStore } from '@/lib/api'
import type { User } from '@/lib/types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** Ustawiane przez MeContextProvider — pełne dane użytkownika z /api/me/context. */
  setUser: (user: User) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Trzyma access token WYŁĄCZNIE w pamięci (CONTRACTS.md §3).
 * Przy starcie próbuje odświeżyć sesję z httpOnly cookie — dzięki temu
 * odświeżenie strony nie wylogowuje użytkownika.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const queryClient = useQueryClient()

  // bootstrap: refresh z cookie
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const token = await authApi.refresh()
      if (cancelled) return
      setStatus(token ? 'authenticated' : 'anonymous')
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // sesja wygasła bezpowrotnie (refresh nie zadziałał)
  useEffect(() => {
    const onExpired = () => {
      setUser(null)
      setStatus('anonymous')
      queryClient.clear()
    }
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired)
  }, [queryClient])

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await authApi.login(email, password)
      if (!data?.access_token) {
        throw new ApiError(500, 'Serwer nie zwrócił tokenu dostępu.', data, '/api/auth/login')
      }
      tokenStore.set(data.access_token)
      setUser(data.user ?? null)
      setStatus('authenticated')
      await queryClient.invalidateQueries()
    },
    [queryClient],
  )

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
    setStatus('anonymous')
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, setUser }),
    [status, user, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth musi być użyty wewnątrz <AuthProvider>')
  return context
}
